import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages } from "../../../constants/menuLanguages";
import { fetchSubcategoriesByCategory } from "../../../services/subcategories";
import { productTitle, subcategoryTitle } from "../Cms/MenuTree/menuTreeUtils";
import "../../../i18n";
import "./AddCategory.css";

const categoryPhotoUrl = (photo) => `${config.UPLOADS_SERVER}/category/${photo}`;

const pickLocalized = (details, lang, field, fallback = "") => {
  const list = Array.isArray(details) ? details : [];
  const match = list.find((d) => d?.language === lang && String(d?.[field] || "").trim());
  if (match?.[field]) return match[field];
  const en = list.find((d) => d?.language === "en" && String(d?.[field] || "").trim());
  if (en?.[field]) return en[field];
  const first = list.find((d) => String(d?.[field] || "").trim());
  return first?.[field] || fallback;
};

const CategoryDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const languages = useMemo(() => getMenuLanguages(t), [t]);

  const [category, setCategory] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openLangs, setOpenLangs] = useState({});
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [categoryRes, assigned] = await Promise.all([
          axios.get(`${config.dev}/v2/categories/retrieve/${id}`),
          fetchSubcategoriesByCategory(id).catch(() => []),
        ]);
        if (cancelled) return;
        const data = categoryRes?.data || {};
        setCategory(data);
        setSubs(Array.isArray(assigned) ? assigned : []);
        setOpenLangs({});
      } catch (err) {
        console.error("Error fetching category:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) => setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));
  const usedLangs = languages.filter((lang) =>
    (category?.categoryDetails || []).some(
      (d) =>
        d.language === lang.code &&
        String(d.categoryTitle || d.categoryDescription || "").trim()
    )
  );
  const langsToShow = usedLangs.length
    ? usedLangs
    : languages.filter((lang) => lang.code === "en");

  const photoSrc = category?.photo ? categoryPhotoUrl(category.photo) : null;
  const fallbackTitle = pickLocalized(
    category?.categoryDetails,
    "en",
    "categoryTitle",
    t("categoryDetails")
  );
  const orientation = category?.displayOrientation === "horizontal" ? "horizontal" : "vertical";

  const renderDescription = (html) => {
    const cleaned = DOMPurify.sanitize(html || "");
    const text = cleaned.replace(/<[^>]+>/g, "").trim();
    if (!text) {
      return <p className="catform__muted">{t("noDescription", { defaultValue: "No description" })}</p>;
    }
    return <div className="catform__prose" dangerouslySetInnerHTML={{ __html: cleaned }} />;
  };

  const productsOf = (sub) =>
    (sub?.productList || [])
      .filter((p) => p?.product != null && p.product.isArchived !== true)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((p) => p.product);

  if (loading) {
    return (
      <>
        <PageTitle activeMenu={t("categoryDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  if (!category?._id) {
    return (
      <>
        <PageTitle activeMenu={t("categoryDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("noTitle")}</p>
          <div className="catform__actions">
            <button
              type="button"
              className="btn catform__btn catform__btn--cancel"
              onClick={() => navigate("/categories")}
            >
              {t("categoriesList")}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={t("categoryDetails")} motherMenu={t("kitchen")} />

      <div className="catform catform--sheet">
        <div className="catform__top catform__identity">
          <button
            type="button"
            className={`catform__cover${photoSrc ? "" : " is-empty"}`}
            onClick={() => photoSrc && setLightbox(true)}
            aria-label={photoSrc ? t("catFormPhoto") : t("prodSheetNoPhoto")}
          >
            {photoSrc ? <img src={photoSrc} alt="" /> : <i className="fas fa-image" aria-hidden />}
          </button>
          <div className="catform__identity-copy">
            <p className="catform__kicker">{t("catSheetSubtitle")}</p>
            <h1 className="catform__title">{fallbackTitle}</h1>
            <div className="catform__facts">
              <span className="catform__fact">{t(orientation)}</span>
              <span className="catform__fact">
                {subs.length} {t("catFormStepSubcategories").toLowerCase()}
              </span>
            </div>
          </div>
          <div className="catform__top-actions">
            <button type="button" className="btn catform__link" onClick={() => navigate("/categories")}>
              <i className="fas fa-arrow-left" aria-hidden /> {t("categoriesList")}
            </button>
            <Link to={`/edit-category/${id}`} className="btn catform__btn catform__btn--edit">
              <i className="fas fa-pen" aria-hidden />
              {t("edit")}
            </Link>
          </div>
        </div>

        <section className="catform__section is-open">
          <h2 className="catform__heading">{t("catSheetLanguages")}</h2>
          <p className="catform__help">{t("sheetLangHint")}</p>
          <div className="catform__pills">
            {langsToShow.map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={`catform__pill is-filled ${isLangOpen(lang.code) ? "is-open" : ""}`}
                aria-pressed={isLangOpen(lang.code)}
                onClick={() => toggleLang(lang.code)}
              >
                <img src={lang.flag} alt="" />
                {lang.label}
                <i className="fas fa-check" aria-hidden />
              </button>
            ))}
          </div>
          <div className="catform__lang-fields">
            {langsToShow
              .filter((lang) => isLangOpen(lang.code))
              .map((lang) => {
                const title = pickLocalized(
                  category.categoryDetails,
                  lang.code,
                  "categoryTitle",
                  t("noTitle")
                );
                const description = pickLocalized(
                  category.categoryDetails,
                  lang.code,
                  "categoryDescription"
                );
                return (
                  <div
                    className="catform__lang-panel"
                    key={lang.code}
                    dir={lang.code === "ar" ? "rtl" : undefined}
                  >
                    <div className="catform__field">
                      <span className="catform__label">
                        <img src={lang.flag} alt="" />
                        {lang.label} — {t("title")}
                      </span>
                      <p className="catform__readonly">{title}</p>
                    </div>
                    <div className="catform__field">
                      <span className="catform__label">{t("description")}</span>
                      {renderDescription(description)}
                    </div>
                  </div>
                );
              })}
          </div>
        </section>

        <section className="catform__section is-open">
          <h2 className="catform__heading">
            {t("catFormStepSubcategories")}
            {subs.length > 0 && <span className="catform__fold-count">{subs.length}</span>}
          </h2>

          <div className="prod-choices__list catform__sheet-choices">
            {subs.length === 0 ? (
              <div className="prod-choices__empty">
                <i className="fas fa-folder-open" aria-hidden />
                <p>{t("catSheetNoSubs")}</p>
              </div>
            ) : (
              subs.map((sub) => {
                const products = productsOf(sub);
                return (
                  <article
                    key={String(sub._id)}
                    className={`prod-choice is-checked${products.length > 0 ? " is-open" : ""}`}
                  >
                    <div className="prod-choice__head">
                      <span className="prod-choice__name">{subcategoryTitle(sub)}</span>
                      {products.length > 0 && (
                        <span className="prod-choice__count">{products.length}</span>
                      )}
                    </div>
                    {products.length > 0 && (
                      <div className="prod-choice__body">
                        <div className="catform__size-chips">
                          {products.map((product) => (
                            <span key={String(product._id)} className="prod-choice__chip">
                              {productTitle(product)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })
            )}
          </div>
        </section>

        <div className="catform__actions">
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => navigate("/categories")}
          >
            {t("categoriesList")}
          </button>
          <Link to={`/edit-category/${id}`} className="btn catform__btn catform__btn--edit">
            <i className="fas fa-pen" aria-hidden />
            {t("edit")}
          </Link>
        </div>
      </div>

      {lightbox && photoSrc && (
        <div className="catform-lightbox" onClick={() => setLightbox(false)} role="dialog" aria-modal="true">
          <button type="button" className="catform-lightbox__close" aria-label={t("cancel")}>
            <i className="fas fa-times" />
          </button>
          <img src={photoSrc} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

export default CategoryDetails;
